import { useTranslation } from "react-i18next";
import { CheckCircle } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import dashboardImg from "../assets/dashboard.png?w=1200&format=webp";
import dashboardSrcSet from "../assets/dashboard.png?w=400;800;1200&format=webp&as=srcset";

const Hero = ({ audience, onAudienceChange, onContactClick }) => {
  const { t, i18n } = useTranslation();
  const isTalent = audience === "talent";
  const ctaLabel = isTalent ? t("hero.ctaTalent") : t("hero.ctaHiring");
  const headline1Key = isTalent ? "hero.talentHeadline1" : "hero.headline1";
  const headline2Key = isTalent ? "hero.talentHeadline2" : "hero.headline2";
  const subheadlineKey = isTalent
    ? "hero.talentSubheadline"
    : "hero.subheadline";

  // Animation state
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayAudience, setDisplayAudience] = useState(audience);
  const [animationKey, setAnimationKey] = useState(0);
  const prevAudienceRef = useRef(audience);
  const heroRef = useRef(null);

  // Falling documents animation state
  const [scrollProgress, setScrollProgress] = useState(0);
  const heroMetaRef = useRef({ top: 0, height: 0 });

  // Cache hero's document-relative position; update on resize (post-layout, no reflow)
  useEffect(() => {
    const update = () => {
      if (!heroRef.current) return;
      heroMetaRef.current = {
        top: heroRef.current.offsetTop,
        height: heroRef.current.offsetHeight,
      };
    };
    update();
    const ro = new ResizeObserver(update);
    if (heroRef.current) ro.observe(heroRef.current);
    return () => ro.disconnect();
  }, []);

  // Documents scroll animation - belgeler scroll ile aşağı düşer
  // window.scrollY doesn't force reflow; uses cached hero position
  useEffect(() => {
    const handleScroll = () => {
      const { top, height } = heroMetaRef.current;
      const scrollY = window.scrollY;
      const scrolledInHero = scrollY - top;

      // Scroll progress: 0 = başlangıç, 1 = tam scroll
      if (scrollY >= top && scrollY < top + height) {
        const progress = Math.min(Math.max(scrolledInHero / (height * 0.7), 0), 1);
        setScrollProgress(progress);
      } else if (scrollY < top) {
        setScrollProgress(0);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Ana yığın: Çizgi üzerinde birlikte yukarı hareket eden belgeler (5 adet)
  const mainStackCount = 5;

  // Düşen belgeler: Belirli scroll noktalarında yığından kopup aşağı düşenler
  // Daha yavaş (fallSpeed düşük) ve daha sık (daha fazla belge)
  const fallingDocs = [
    { triggerAt: 0.1, fallDirection: 1, rotateEnd: 35, fallSpeed: 0.4 }, // 1. Sağa düşer
    { triggerAt: 0.2, fallDirection: -1, rotateEnd: -40, fallSpeed: 0.35 }, // 2. Sola düşer
    { triggerAt: 0.32, fallDirection: 1, rotateEnd: 45, fallSpeed: 0.45 }, // 3. Sağa düşer
    { triggerAt: 0.44, fallDirection: -1, rotateEnd: -35, fallSpeed: 0.38 }, // 4. Sola düşer
    { triggerAt: 0.55, fallDirection: 1, rotateEnd: 50, fallSpeed: 0.42 }, // 5. Sağa düşer
    { triggerAt: 0.67, fallDirection: -1, rotateEnd: -45, fallSpeed: 0.4 }, // 6. Sola düşer
    { triggerAt: 0.78, fallDirection: 1, rotateEnd: 40, fallSpeed: 0.36 }, // 7. Sağa düşer
  ];

  // CV/Belge SVG komponenti (İşveren için)
  const CVSVG = () => (
    <svg
      width="44"
      height="56"
      viewBox="0 0 44 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="drop-shadow-xl"
    >
      <rect
        x="0"
        y="0"
        width="44"
        height="56"
        rx="3"
        fill="white"
        fillOpacity="0.97"
      />
      <rect
        x="0"
        y="0"
        width="44"
        height="56"
        rx="3"
        fill="none"
        stroke="#E5E7EB"
        strokeWidth="1"
      />
      <path d="M30 0H44V14L30 0Z" fill="#F3F4F6" />
      <path d="M30 0V14H44" fill="none" stroke="#E5E7EB" strokeWidth="0.5" />
      {/* Profil fotoğrafı */}
      <circle cx="14" cy="18" r="7" fill="#00C9A7" fillOpacity="0.15" />
      <circle cx="14" cy="15" r="3" fill="#00C9A7" fillOpacity="0.3" />
      <ellipse cx="14" cy="21" rx="5" ry="3" fill="#00C9A7" fillOpacity="0.3" />
      {/* İsim ve başlık */}
      <rect
        x="24"
        y="12"
        width="16"
        height="3"
        rx="1.5"
        fill="#374151"
        fillOpacity="0.6"
      />
      <rect
        x="24"
        y="17"
        width="12"
        height="2"
        rx="1"
        fill="#9CA3AF"
        fillOpacity="0.4"
      />
      {/* İçerik satırları */}
      <rect x="6" y="30" width="32" height="2" rx="1" fill="#E5E7EB" />
      <rect x="6" y="35" width="28" height="2" rx="1" fill="#E5E7EB" />
      <rect x="6" y="40" width="30" height="2" rx="1" fill="#E5E7EB" />
      <rect x="6" y="45" width="22" height="2" rx="1" fill="#E5E7EB" />
      <rect
        x="6"
        y="50"
        width="10"
        height="2"
        rx="1"
        fill="#00C9A7"
        fillOpacity="0.5"
      />
    </svg>
  );

  // İş İlanı SVG komponenti (Aday için)
  const JobPostingSVG = () => (
    <svg
      width="44"
      height="56"
      viewBox="0 0 44 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="drop-shadow-xl"
    >
      <rect
        x="0"
        y="0"
        width="44"
        height="56"
        rx="3"
        fill="white"
        fillOpacity="0.97"
      />
      <rect
        x="0"
        y="0"
        width="44"
        height="56"
        rx="3"
        fill="none"
        stroke="#E5E7EB"
        strokeWidth="1"
      />
      {/* Üst banner (şirket rengi) */}
      <rect
        x="0"
        y="0"
        width="44"
        height="14"
        rx="3"
        fill="#6366F1"
        fillOpacity="0.8"
      />
      <rect
        x="0"
        y="10"
        width="44"
        height="4"
        fill="#6366F1"
        fillOpacity="0.8"
      />
      {/* Şirket logosu */}
      <circle cx="22" cy="7" r="4" fill="white" fillOpacity="0.9" />
      <rect
        x="20"
        y="5"
        width="4"
        height="4"
        rx="1"
        fill="#6366F1"
        fillOpacity="0.6"
      />
      {/* İş başlığı */}
      <rect
        x="6"
        y="18"
        width="32"
        height="3"
        rx="1.5"
        fill="#374151"
        fillOpacity="0.7"
      />
      {/* Şirket adı */}
      <rect
        x="6"
        y="24"
        width="20"
        height="2"
        rx="1"
        fill="#6366F1"
        fillOpacity="0.5"
      />
      {/* Lokasyon ikonu + metin */}
      <circle cx="9" cy="32" r="2" fill="#9CA3AF" fillOpacity="0.5" />
      <rect x="14" y="31" width="18" height="2" rx="1" fill="#E5E7EB" />
      {/* Maaş/detay satırları */}
      <rect x="6" y="38" width="26" height="2" rx="1" fill="#E5E7EB" />
      <rect x="6" y="43" width="22" height="2" rx="1" fill="#E5E7EB" />
      {/* Başvur butonu */}
      <rect
        x="6"
        y="48"
        width="32"
        height="5"
        rx="2"
        fill="#00C9A7"
        fillOpacity="0.7"
      />
      <rect
        x="14"
        y="49.5"
        width="16"
        height="2"
        rx="1"
        fill="white"
        fillOpacity="0.8"
      />
    </svg>
  );

  // Audience'a göre doğru SVG'yi seç
  const DocumentSVG = isTalent ? JobPostingSVG : CVSVG;

  // Handle smooth transition between audiences
  useEffect(() => {
    if (prevAudienceRef.current !== audience) {
      setIsTransitioning(true);

      // Wait for exit animation
      const exitTimer = setTimeout(() => {
        setDisplayAudience(audience);
        setAnimationKey((prev) => prev + 1);

        // Wait for enter animation to settle
        const enterTimer = setTimeout(() => {
          setIsTransitioning(false);
        }, 100);

        return () => clearTimeout(enterTimer);
      }, 300);

      prevAudienceRef.current = audience;
      return () => clearTimeout(exitTimer);
    }
  }, [audience]);

  const steps = [
    { number: "1", title: t("hero.step1Title"), desc: t("hero.step1Desc") },
    { number: "2", title: t("hero.step2Title"), desc: t("hero.step2Desc") },
    { number: "3", title: t("hero.step3Title"), desc: t("hero.step3Desc") },
  ];

  const talentOffers = [
    {
      title: t("hero.talentOffer1Title"),
      desc: t("hero.talentOffer1Desc"),
      accent: "from-teal-400/60 to-emerald-500/60",
    },
    {
      title: t("hero.talentOffer2Title"),
      desc: t("hero.talentOffer2Desc"),
      accent: "from-indigo-400/60 to-sky-500/60",
    },
    {
      title: t("hero.talentOffer3Title"),
      desc: t("hero.talentOffer3Desc"),
      accent: "from-purple-400/60 to-violet-500/60",
    },
  ];

  return (
    <section
      ref={heroRef}
      className="bg-hero min-h-screen relative overflow-hidden pt-24 sm:pt-28 md:pt-32 lg:pt-40 pb-16 sm:pb-20 md:pb-24 lg:pb-16"
    >
      {/* Decorative Clouds - Responsive positions for all screen sizes */}
      {/* Sağ üst küçük bulut */}
      <div
        className={`absolute right-[20%] lg:right-[25%] hidden md:block transition-all duration-700 ${
          isTransitioning ? "opacity-50" : "opacity-100"
        }`}
        style={{ top: "clamp(80px, 12vh, 140px)" }}
      >
        <svg
          width="90"
          height="45"
          viewBox="0 0 90 45"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <ellipse cx="30" cy="28" rx="18" ry="14" fill="#9AA3E9" />
          <ellipse cx="50" cy="25" rx="22" ry="16" fill="#9AA3E9" />
          <ellipse cx="68" cy="30" rx="15" ry="12" fill="#9AA3E9" />
        </svg>
      </div>

      {/* Sol alt büyük bulut - responsive position */}
      <div
        className={`absolute left-2 lg:left-8 transition-all duration-700 ${
          isTransitioning ? "opacity-50" : "opacity-100"
        }`}
        style={{ top: "clamp(350px, 50vh, 500px)" }}
      >
        <svg
          width="130"
          height="65"
          viewBox="0 0 130 65"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <ellipse cx="32" cy="42" rx="26" ry="18" fill="#9AA3E9" />
          <ellipse cx="65" cy="35" rx="32" ry="23" fill="#9AA3E9" />
          <ellipse cx="98" cy="42" rx="26" ry="18" fill="#9AA3E9" />
        </svg>
      </div>

      {/* Decorative Icon - Audience'a göre farklı görsel */}
      <div
        className={`absolute left-0 hidden md:block z-10 transition-all duration-700 ease-out ${
          isTransitioning ? "opacity-70 scale-95" : "opacity-100 scale-100"
        }`}
        style={{
          top: "clamp(140px, 20vh, 220px)",
        }}
      >
        {isTalent ? (
          /* Adayım için: Parlayan Elmas + Yıldızlar */
          <svg
            viewBox="0 0 200 180"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-32 md:w-40 lg:w-56 xl:w-64 2xl:w-72 h-auto drop-shadow-2xl"
          >
            {/* Arka plan parıltı */}
            <ellipse
              cx="100"
              cy="95"
              rx="70"
              ry="65"
              fill="url(#diamondGlow2)"
              fillOpacity="0.25"
            />

            {/* Elmas üst kısım (taç) */}
            <path d="M100 25L155 60H45L100 25Z" fill="#00E5CC" />
            <path d="M100 25L155 60H100V25Z" fill="#00C9A7" />

            {/* Elmas üst yüzeyler */}
            <path d="M45 60L100 25L68 60H45Z" fill="#5EFCE8" />
            <path d="M155 60L100 25L132 60H155Z" fill="#00B396" />
            <path d="M68 60L100 25L132 60H68Z" fill="#00D9B8" />

            {/* Elmas alt kısım (pavyon) */}
            <path d="M45 60L100 150L68 60H45Z" fill="#00C9A7" />
            <path d="M68 60L100 150L132 60H68Z" fill="#00E5CC" />
            <path d="M132 60L100 150L155 60H132Z" fill="#009E8C" />

            {/* Elmas kenar çizgileri */}
            <path
              d="M45 60H155"
              stroke="#fff"
              strokeWidth="1.5"
              strokeOpacity="0.6"
            />
            <path
              d="M68 60L100 150"
              stroke="#fff"
              strokeWidth="0.5"
              strokeOpacity="0.3"
            />
            <path
              d="M132 60L100 150"
              stroke="#fff"
              strokeWidth="0.5"
              strokeOpacity="0.3"
            />

            {/* Elmas üzerindeki parıltılar */}
            <circle cx="80" cy="48" r="5" fill="white" fillOpacity="0.9" />
            <circle cx="68" cy="55" r="2.5" fill="white" fillOpacity="0.7" />
            <circle cx="90" cy="40" r="2" fill="white" fillOpacity="0.6" />

            {/* Sol üst yıldız */}
            <path
              d="M22 40L25 47L32 47L27 52L29 60L22 55L15 60L17 52L12 47L19 47Z"
              fill="#FFD700"
            />
            <circle cx="22" cy="50" r="8" fill="#FFD700" fillOpacity="0.15" />

            {/* Sağ üst yıldız */}
            <path
              d="M178 30L181 37L188 37L183 42L185 50L178 45L171 50L173 42L168 37L175 37Z"
              fill="#FFD700"
            />
            <circle cx="178" cy="40" r="8" fill="#FFD700" fillOpacity="0.15" />

            {/* Sol alt küçük yıldız */}
            <path
              d="M18 110L20 115L25 115L21 118L23 123L18 120L13 123L15 118L11 115L16 115Z"
              fill="#FFD700"
              fillOpacity="0.8"
            />

            {/* Sağ alt küçük yıldız */}
            <path
              d="M182 100L184 105L189 105L185 108L187 113L182 110L177 113L179 108L175 105L180 105Z"
              fill="#FFD700"
              fillOpacity="0.8"
            />

            {/* Üst orta mini yıldız */}
            <path
              d="M140 12L141.5 16L146 16L142.5 18.5L144 23L140 20L136 23L137.5 18.5L134 16L138.5 16Z"
              fill="#FFD700"
              fillOpacity="0.7"
            />

            {/* Işık çizgileri */}
            <path
              d="M25 75L35 75"
              stroke="#FFD700"
              strokeWidth="2"
              strokeLinecap="round"
              strokeOpacity="0.6"
            />
            <path
              d="M165 75L175 75"
              stroke="#FFD700"
              strokeWidth="2"
              strokeLinecap="round"
              strokeOpacity="0.6"
            />
            <path
              d="M30 135L38 128"
              stroke="#FFD700"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeOpacity="0.5"
            />
            <path
              d="M170 135L162 128"
              stroke="#FFD700"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeOpacity="0.5"
            />

            {/* Gradient tanımı */}
            <defs>
              <radialGradient id="diamondGlow2" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#00E5CC" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#00E5CC" stopOpacity="0" />
              </radialGradient>
            </defs>
          </svg>
        ) : (
          /* İşverenim için: Büyüteç + Profil (yetenek arayışı) */
          <svg
            viewBox="0 0 200 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-32 md:w-40 lg:w-56 xl:w-64 2xl:w-72 h-auto drop-shadow-2xl"
          >
            {/* Büyüteç sapı */}
            <rect
              x="135"
              y="135"
              width="50"
              height="16"
              rx="8"
              fill="#1E3A5F"
              transform="rotate(45 135 135)"
            />
            <rect
              x="138"
              y="138"
              width="40"
              height="10"
              rx="5"
              fill="#2C5282"
              transform="rotate(45 138 138)"
            />

            {/* Büyüteç çerçevesi (dış) */}
            <circle cx="85" cy="85" r="60" fill="#1E3A5F" />
            <circle cx="85" cy="85" r="52" fill="#2C5282" />

            {/* Büyüteç camı */}
            <circle cx="85" cy="85" r="45" fill="url(#lensGradient)" />

            {/* Cam içindeki profil silüeti */}
            <circle cx="85" cy="70" r="18" fill="#00C9A7" fillOpacity="0.9" />
            <ellipse
              cx="85"
              cy="105"
              rx="25"
              ry="18"
              fill="#00C9A7"
              fillOpacity="0.9"
            />

            {/* Profil detayları */}
            <circle cx="85" cy="70" r="14" fill="#00D9B8" />
            <ellipse cx="85" cy="102" rx="20" ry="14" fill="#00D9B8" />

            {/* Yüz özellikleri (basit) */}
            <circle cx="80" cy="68" r="2" fill="#1E3A5F" fillOpacity="0.6" />
            <circle cx="90" cy="68" r="2" fill="#1E3A5F" fillOpacity="0.6" />
            <path
              d="M82 76C82 76 85 79 88 76"
              stroke="#1E3A5F"
              strokeWidth="1.5"
              strokeOpacity="0.5"
              strokeLinecap="round"
            />

            {/* Cam parlaması */}
            <path
              d="M55 55C65 45 85 42 100 50"
              stroke="white"
              strokeWidth="4"
              strokeOpacity="0.4"
              strokeLinecap="round"
            />
            <circle cx="60" cy="60" r="5" fill="white" fillOpacity="0.5" />

            {/* Onay işareti (bulundu!) */}
            <circle cx="140" cy="45" r="18" fill="#00C9A7" />
            <path
              d="M132 45L138 51L150 39"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Yıldızlar */}
            <circle cx="45" cy="35" r="3" fill="#FFD700" fillOpacity="0.8" />
            <circle cx="155" cy="90" r="2" fill="#FFD700" fillOpacity="0.6" />
            <path
              d="M30 75L33 80L38 80L34 83L36 88L30 85L24 88L26 83L22 80L27 80Z"
              fill="#FFD700"
              fillOpacity="0.7"
            />

            {/* Gradient tanımı */}
            <defs>
              <radialGradient id="lensGradient" cx="30%" cy="30%" r="70%">
                <stop offset="0%" stopColor="#E8F4F8" />
                <stop offset="100%" stopColor="#B8D4E3" />
              </radialGradient>
            </defs>
          </svg>
        )}
      </div>

      {/* Document Stack Trail - Sağ tarafta dikey çizgi ve yığından düşen belgeler */}
      <div className="absolute right-8 lg:right-16 xl:right-24 2xl:right-32 top-0 bottom-0 hidden lg:block pointer-events-none">
        {/* Dikey kesikli çizgi */}
        <div
          className="absolute left-1/2 -translate-x-1/2 w-0.5"
          style={{
            top: "8%",
            height: "84%",
            backgroundImage:
              "repeating-linear-gradient(to bottom, rgba(255,255,255,0.4) 0px, rgba(255,255,255,0.4) 12px, transparent 12px, transparent 24px)",
          }}
        />

        {/* Üst bulut */}
        <div className="absolute -left-14 top-[15%]">
          <svg
            width="60"
            height="30"
            viewBox="0 0 60 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <ellipse cx="18" cy="18" rx="12" ry="9" fill="#9AA3E9" />
            <ellipse cx="32" cy="15" rx="15" ry="11" fill="#9AA3E9" />
            <ellipse cx="46" cy="18" rx="10" ry="8" fill="#9AA3E9" />
          </svg>
        </div>

        {/* Alt bulut */}
        <div className="absolute -left-16 top-[30%]">
          <svg
            width="70"
            height="35"
            viewBox="0 0 70 35"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <ellipse cx="20" cy="22" rx="14" ry="10" fill="#9AA3E9" />
            <ellipse cx="38" cy="18" rx="18" ry="13" fill="#9AA3E9" />
            <ellipse cx="54" cy="22" rx="12" ry="9" fill="#9AA3E9" />
          </svg>
        </div>

        {/* Ana Belge Yığını - Çizgi üzerinde birlikte yukarı hareket eder */}
        <div
          className="absolute left-1/2 -translate-x-1/2 transition-all duration-300 ease-out"
          style={{
            bottom: `${12 + scrollProgress * 55}%`, // Altta başlar, yukarı çıkar
            width: "50px",
          }}
        >
          {/* Yığındaki belgeler (üst üste dizili) */}
          {[...Array(mainStackCount)].map((_, idx) => (
            <div
              key={`stack-${idx}`}
              className="absolute"
              style={{
                bottom: `${idx * 5}px`,
                left: `${idx % 2 === 0 ? -1 : 1}px`, // Hafif zigzag
                transform: `rotate(${idx % 2 === 0 ? -2 : 2}deg)`,
                zIndex: mainStackCount - idx,
              }}
            >
              <DocumentSVG />
            </div>
          ))}
        </div>

        {/* Düşen Belgeler - Yığından kopup aşağı düşenler */}
        {fallingDocs.map((doc, idx) => {
          // Bu belge düşmeye başladı mı?
          const hasTriggered = scrollProgress >= doc.triggerAt;
          // Düşme ilerlemesi (0-1) - daha yavaş düşme için 0.5 kullanıyoruz
          const fallProgress = hasTriggered
            ? Math.min(
                ((scrollProgress - doc.triggerAt) / 0.5) * doc.fallSpeed,
                1,
              )
            : 0;

          // Düşme pozisyonu hesaplama
          const startY = 12 + doc.triggerAt * 55; // Yığının o anki pozisyonu (%)
          const fallY = fallProgress * 350; // Aşağı düşme (px)
          const fallX = fallProgress * doc.fallDirection * 180; // Sağa/sola kayma (px) - daha geniş açı
          const rotation = fallProgress * doc.rotateEnd;
          const opacity =
            fallProgress > 0.75 ? 1 - (fallProgress - 0.75) * 3.5 : 1;

          if (!hasTriggered) return null;

          return (
            <div
              key={`fall-${idx}`}
              className="absolute left-1/2 transition-all duration-300 ease-out"
              style={{
                bottom: `calc(${startY}% - ${fallY}px)`,
                transform: `translateX(calc(-50% + ${fallX}px)) rotate(${rotation}deg)`,
                opacity: Math.max(0, opacity),
                zIndex: 5,
              }}
            >
              <DocumentSVG />
            </div>
          );
        })}
      </div>

      {/* Main Content */}
      <div className="container-custom pb-12 sm:pb-16 md:pb-20 relative z-10 px-4 sm:px-6">
        <div className="text-center max-w-4xl mx-auto">
          {/* Audience Toggle - Enhanced */}
          <div className="flex justify-center mb-6 sm:mb-6 md:mb-8">
            <div className="relative inline-flex items-center gap-1 rounded-full border border-white/30 bg-white/10 px-1 sm:px-1.5 py-1 sm:py-1.5 backdrop-blur-lg shadow-lg">
              {/* Animated background pill */}
              <div
                className={`absolute top-1 sm:top-1.5 bottom-1 sm:bottom-1.5 rounded-full bg-white shadow-lg transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                  isTalent ? "translate-x-[calc(100%+4px)]" : "translate-x-0"
                }`}
                style={{
                  width: "calc(50% - 6px)",
                  left: "6px",
                }}
              />
              <button
                onClick={() => onAudienceChange?.("hiring")}
                className={`relative z-10 px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm md:text-base font-semibold transition-all duration-300 touch-manipulation ${
                  !isTalent ? "text-primary" : "text-white/80 hover:text-white"
                }`}
                aria-current={!isTalent ? "page" : undefined}
              >
                <span
                  className={`inline-block transition-transform duration-300 ${!isTalent ? "scale-105" : "scale-100"}`}
                >
                  {t("hero.toggleHiring")}
                </span>
              </button>
              <button
                onClick={() => onAudienceChange?.("talent")}
                className={`relative z-10 px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm md:text-base font-semibold transition-all duration-300 touch-manipulation ${
                  isTalent ? "text-primary" : "text-white/80 hover:text-white"
                }`}
              >
                <span
                  className={`inline-block transition-transform duration-300 ${isTalent ? "scale-105" : "scale-100"}`}
                >
                  {t("hero.toggleTalent")}
                </span>
              </button>
            </div>
          </div>

          {/* Headline */}
          <h1 className="hero-headline text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-white mb-6 sm:mb-6 md:mb-6 px-2">
            {t(headline1Key)}
            <br />
            <span className="text-accent-teal">{t(headline2Key)}</span>
          </h1>

          {/* Subheadline */}
          <p className="text-base sm:text-lg md:text-xl text-white max-w-2xl mx-auto mb-8 sm:mb-8 md:mb-10 px-2">
            {t(subheadlineKey)}
          </p>

          {/* Badge */}
          {!isTalent && (
            <div className="flex justify-center mb-8 sm:mb-10">
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-r from-accent-teal/60 via-violet-500/50 to-accent-teal/60 rounded-2xl blur-xl animate-pulse" />
                <div className="absolute -inset-1 bg-gradient-to-r from-accent-teal/30 to-violet-500/30 rounded-xl blur-sm" />
                <span className="relative inline-flex items-center gap-3 bg-gradient-to-r from-accent-teal/30 via-indigo-500/25 to-violet-500/30 border-2 border-accent-teal/60 backdrop-blur-md rounded-xl px-8 py-3.5 text-base sm:text-lg md:text-xl font-extrabold text-white shadow-2xl shadow-accent-teal/30 tracking-wide">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inset-0 rounded-full bg-accent-teal opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-accent-teal shadow-lg shadow-accent-teal" />
                  </span>
                  {t('hero.heroBadge')}
                </span>
              </div>
            </div>
          )}

          {/* CTA Button */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-10 sm:mb-8 md:mb-12">
            <button
              onClick={(e) => {
                e.preventDefault();
                if (isTalent) {
                  window.open("https://isbulsun.com", "_blank", "noopener,noreferrer");
                } else {
                  window.open(
                    "https://admin.hirethink.ai/register",
                    "_blank",
                    "noopener,noreferrer",
                  );
                }
              }}
              className="btn-primary text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 w-full sm:w-auto touch-manipulation max-w-xs sm:max-w-none"
            >
              {ctaLabel}
            </button>
          </div>
        </div>

        {/* Steps & Screenshot Section with enhanced animated swap */}
        <div className="relative mt-8 sm:mt-8 md:mt-12 lg:mt-16 perspective-container min-h-[240px] sm:min-h-[280px] md:min-h-[320px] lg:min-h-[400px] xl:min-h-[450px] pb-4 sm:pb-0">
          {/* Animated background glow */}
          <div className="absolute inset-0 pointer-events-none">
            <div
              className={`absolute inset-0 transition-all duration-700 blur-3xl ${
                isTalent
                  ? "bg-gradient-to-br from-teal-500/10 via-indigo-500/10 to-purple-500/10"
                  : "bg-gradient-to-br from-white/5 to-white/0"
              }`}
            />
          </div>

          {/* Content wrapper with transition - smooth height change */}
          <div
            key={animationKey}
            className={`transition-all duration-500 ease-out ${isTransitioning ? "opacity-0 scale-[0.98]" : "opacity-100 scale-100"}`}
          >
            {displayAudience === "talent" ? (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 items-stretch">
                {talentOffers.map((offer, idx) => (
                  <div
                    key={offer.title}
                    className={`group relative rounded-xl sm:rounded-2xl bg-white/10 border border-white/15 backdrop-blur-lg p-4 sm:p-6 text-left overflow-hidden transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:bg-white/15 h-full flex flex-col animate-talent-card`}
                    style={{ animationDelay: `${idx * 100}ms` }}
                  >
                    {/* Hover glow effect */}
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${offer.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-2xl`}
                    />

                    {/* Shimmer effect on hover */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                    </div>

                    <div className="relative flex items-start gap-2 sm:gap-3">
                      <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/20 border border-white/30 flex items-center justify-center text-white font-semibold group-hover:scale-110 group-hover:bg-white/30 transition-all duration-300">
                        <span className="group-hover:animate-pulse text-sm sm:text-base">
                          ✦
                        </span>
                      </div>
                      <div>
                        <h2 className="text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2 group-hover:text-accent-teal transition-colors duration-300">
                          {offer.title}
                        </h2>
                        <p className="text-sm sm:text-base text-white/80 leading-relaxed">
                          {offer.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 md:gap-12 lg:gap-20 items-stretch">
                {/* Steps with staggered animation */}
                <div className="space-y-6 sm:space-y-8">
                  {steps.map((step, idx) => (
                    <div
                      key={step.number}
                      className="flex items-start gap-3 sm:gap-4 animate-hiring-step"
                      style={{ animationDelay: `${idx * 120}ms` }}
                    >
                      <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-accent-teal text-white flex items-center justify-center font-bold text-xs sm:text-sm shadow-lg shadow-accent-teal/30 hover:scale-110 transition-transform duration-300">
                        {step.number}
                      </div>
                      <div>
                        <h2 className="text-lg sm:text-xl font-bold text-white mb-1">
                          {step.title}
                        </h2>
                        <p className="text-sm sm:text-base text-white/70">
                          {step.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Dashboard Screenshot with enhanced animation */}
                <div className="relative h-full animate-hiring-screenshot">
                  {/* Main Screenshot Card */}
                  <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-3 sm:p-4 lg:p-5 relative z-10 h-full hover:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.3)] transition-shadow duration-500">
                    <div className="bg-gray-50 rounded-xl sm:rounded-2xl overflow-hidden border border-gray-200 flex items-center justify-center h-full min-h-[200px] sm:min-h-[280px] md:min-h-[360px] lg:min-h-[420px]">
                      <img
                        src={dashboardImg}
                        srcSet={dashboardSrcSet}
                        sizes="(max-width: 640px) calc(100vw - 3rem), (max-width: 1024px) 55vw, 800px"
                        alt={t("hero.dashboardPreview")}
                        className="w-full h-auto max-h-[300px] sm:max-h-[400px] md:max-h-[520px] lg:max-h-[560px] object-contain"
                        width="800"
                        height="560"
                        loading="eager"
                        decoding="async"
                        fetchpriority="high"
                      />
                    </div>
                  </div>

                  {/* Floating Stats Card - Enhanced */}
                  <div
                    className="absolute -top-16 sm:-top-20 -right-4 sm:-right-6 bg-white rounded-lg sm:rounded-xl shadow-xl p-3 sm:p-4 z-20 hidden md:block float-slow animate-card-enter hover:scale-105 transition-transform duration-300"
                    style={{ animationDelay: "400ms" }}
                  >
                    <div className="text-xs sm:text-sm font-medium text-gray-500 mb-1">
                      {t("hero.totalEvaluations")}
                    </div>
                    <div className="text-xl sm:text-2xl font-bold text-primary">
                      2,847
                    </div>
                    <div className="flex items-center gap-1 text-accent-teal text-xs sm:text-sm">
                      <span>↑ 12%</span>
                      <span className="text-gray-400">
                        {t("hero.thisMonth")}
                      </span>
                    </div>
                  </div>

                  {/* Floating Success Card - Enhanced */}
                  <div
                    className="absolute -bottom-4 sm:-bottom-6 -left-4 sm:-left-6 bg-white rounded-lg sm:rounded-xl shadow-xl p-3 sm:p-4 z-20 hidden md:block float-medium animate-card-enter hover:scale-105 transition-transform duration-300"
                    style={{ animationDelay: "550ms" }}
                  >
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-accent-teal/10 flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-accent-teal" />
                      </div>
                      <div>
                        <div className="text-xs sm:text-sm font-medium text-gray-700">
                          {t("hero.interviewCompleted")}
                        </div>
                        <div className="text-xs text-gray-400">
                          {t("hero.minutesAgo")}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Wave Divider */}
      <div className="wave-divider">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path
            d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C57.1,118.92,156.63,69.08,321.39,56.44Z"
            fill="#ffffff"
          />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
